import { useEffect, useState } from "react";

import Box from "@/components/Box";
import Divider from "@/components/Divider";
import { ExternalTextLink } from "@/components/Links";
import {
  CMP_PRACTITIONER_URL,
  CMP_SIGNIN_URL,
  CMP_STUDENT_APPLICATION_URL,
  EXPERTS_OVERVIEW_URL,
  EXPERTS_URL,
  LOGIN_URL
} from "@/core/urls";
import RegenerusLogo from "@/images/RegenerusLogoWithoutDots";
import Badge from "@/tpo/Badge";
import { PanelBoxV2 } from "@/tpo/Boxes";
import ChevronComponent from "@/tpo/Chevron";
import Group from "@/tpo/Group";
import LinkWrapper from "@/tpo/LinkWrapper";
import Menu from "@/tpo/Menu";
import ButtonV2 from "@/v2/Buttons";

import styles from "../common/NavLink.module.css";
import LoggedOutBurgerMenu from "../practitioner/PractitionerLoggedOutBurgerMenu";

function ExternalNavLink({ text, display, href, underline }) {
  return (
    <Box
      className={styles.navLink}
      as={ExternalTextLink}
      color="white"
      fontFamily="gilroyMedium"
      fontSize={16}
      px={16}
      height="100%"
      display={display}
      alignItems="center"
      lineHeight="150%"
      href={href}
      underline={underline}
      // same tab
      target={null}
      rel={null}
    >
      {text}
    </Box>
  );
}

function InternalNavLink({ text, display, href }) {
  return (
    <Box
      className={styles.navLink}
      as={LinkWrapper}
      to={href}
      color="white"
      fontFamily="gilroyMedium"
      fontSize={16}
      px={16}
      height="100%"
      display={display}
      alignItems="center"
      lineHeight="150%"
    >
      {text}
    </Box>
  );
}

export default function LoggedOutNavigation() {
  const [aboutMenuOpen, setAboutMenuOpen] = useState(false);
  const [signInMenuOpen, setSignInMenuOpen] = useState(false);

  return (
    <PanelBoxV2
      maxWidth={1538}
      outer={{
        px: [20],
        position: "fixed",
        top: 40,
        left: 0,
        right: 0,
        bg: "transparent",
        zIndex: 4,
        "data-component-name": "LoggedOutNavHeader"
      }}
      inner={{
        height: [48, 48, 60]
      }}
    >
      <Group
        alignItems="center"
        bg="dark"
        justifyContent="space-between"
        borderRadius="10px"
        px={[16, 16, 20]}
        height="100%"
      >
        {/* Regenerus Logo on Left */}
        <Group alignItems="center" justifyContent="flex-start">
          <RegenerusLogo fill="white" width={[125, 158, 215]} height={30} />
        </Group>

        {/* Navigation Links and Button on Right */}
        <Group alignItems="center" justifyContent="flex-end" height="100%">
          {/* Practitioners Link */}
          <ExternalNavLink
            text="Practitioners"
            display={["none", "none", "flex"]}
            href="https://www.regeneruslabs.me/practitioners"
            underline={false}
          />

          {/* Patients Link */}
          <InternalNavLink
            text="Patients"
            display={["none", "none", "flex"]}
            href={EXPERTS_OVERVIEW_URL}
          />

          {/* Platform Link */}
          <ExternalNavLink
            text="Platform"
            display={["none", "none", "flex"]}
            href="https://www.regeneruslabs.com/practitioner-portal"
            underline={false}
          />

          {/* Tests Link */}
          <ExternalNavLink
            text="Tests"
            display={["none", "none", "flex"]}
            href="https://www.regeneruslabs.com/tests"
            underline={false}
          />

          {/* About Dropdown */}
          <Menu
            opened={aboutMenuOpen}
            onChange={setAboutMenuOpen}
            trigger="hover"
            placement="bottom-start"
            allowedPlacements={["bottom-start"]}
            xOffset={0}
          >
            <Menu.Target>
              <Box
                display={["none", "none", "flex"]}
                alignItems="center"
                gap={10}
                color="white"
                fontFamily="gilroyMedium"
                fontSize={16}
                lineHeight="150%"
                px={16}
                borderBottom="2px solid transparent"
                bg={aboutMenuOpen ? "#282828" : "dark"}
                height="100%"
              >
                About
                <ChevronComponent
                  direction={aboutMenuOpen ? "top" : "bottom"}
                  fill="white"
                  width={5}
                />
              </Box>
            </Menu.Target>
            <Menu.Dropdown
              minWidth={200}
              borderRadius={5}
              borderTopLeftRadius={aboutMenuOpen ? 0 : 5}
              borderTopRightRadius={aboutMenuOpen ? 0 : 5}
              data-about-dropdown="true"
              bg="#282828"
              px="8px"
              py={28}
            >
              <Menu.Item
                p="8px"
                color="white"
                fontFamily="gilroyBold"
                fontSize={16}
                lineHeight="150%"
                borderRadius="4px"
                selectedColor="rgba(255, 255, 255, 0.1)"
                as={ExternalTextLink}
                href="https://www.regeneruslabs.com/about"
                underline={false}
                display="block"
                target={null}
                rel={null}
              >
                Our vision
              </Menu.Item>
              <Menu.Item
                p="8px"
                color="white"
                fontFamily="gilroyBold"
                fontSize={16}
                lineHeight="150%"
                borderRadius="4px"
                selectedColor="rgba(255, 255, 255, 0.1)"
                as={ExternalTextLink}
                href="https://www.regeneruslabs.com/contact"
                underline={false}
                display="block"
                target={null}
                rel={null}
              >
                Contact us
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>

          {/* Sign in / Sign up Button with Dropdown */}
          <Menu
            opened={signInMenuOpen}
            onChange={setSignInMenuOpen}
            trigger="hover"
            placement="bottom-end"
            allowedPlacements={["bottom-end"]}
            yOffset={0}
            xOffset={0}
          >
            <Menu.Target>
              <ButtonV2
                variant="filled"
                color="green"
                fontFamily="gilroyBold"
                sx={{
                  borderBottomLeftRadius: signInMenuOpen ? 0 : undefined,
                  borderBottomRightRadius: signInMenuOpen ? 0 : undefined,
                  px: "16px",
                  py: "4px",
                  borderRadius: "4px",
                  height: "32px",
                  display: "flex",
                  textTransform: "unset",
                  letterSpacing: "unset",
                  fontSize: 14,
                  ml: "10px"
                }}
              >
                Sign in / Sign up
              </ButtonV2>
            </Menu.Target>
            <Menu.Dropdown
              bg="green"
              borderRadius={0}
              borderTopLeftRadius={0}
              borderTopRightRadius={0}
              borderBottomLeftRadius={5}
              borderBottomRightRadius={5}
              pt={"10px"}
              pb={["8px"]}
              px="8px"
            >
              <Box>
                <Badge bg="dark" color="white" px="8px" py="4px">
                  Sign in
                </Badge>
              </Box>
              <Menu.Item
                py="4px"
                px="8px"
                color="white"
                fontFamily="gilroyBold"
                fontSize={14}
                as={LinkWrapper}
                to={CMP_SIGNIN_URL}
                display="block"
                selectedColor="rgba(255, 255, 255, 0.10)"
                borderRadius="4px"
              >
                Practitioners
              </Menu.Item>
              <Menu.Item
                py="4px"
                px="8px"
                color="white"
                fontFamily="gilroyBold"
                fontSize={14}
                as={LinkWrapper}
                to={LOGIN_URL}
                display="block"
                selectedColor="rgba(255, 255, 255, 0.10)"
                borderRadius="4px"
              >
                Patients
              </Menu.Item>
              <Divider color="rgba(255, 255, 255, 0.30)" my="8px" />
              <Box>
                <Badge bg="dark" color="white" px="8px" py="4px" my="4px">
                  Sign up
                </Badge>
              </Box>
              <Menu.Item
                py="4px"
                px="8px"
                color="white"
                fontFamily="gilroyBold"
                fontSize={14}
                as={LinkWrapper}
                to={CMP_PRACTITIONER_URL}
                display="block"
                selectedColor="rgba(255, 255, 255, 0.10)"
                borderRadius="4px"
              >
                Practitioners
              </Menu.Item>
              <Menu.Item
                py="4px"
                px="8px"
                color="white"
                fontFamily="gilroyBold"
                fontSize={14}
                as={LinkWrapper}
                to={CMP_STUDENT_APPLICATION_URL}
                display="block"
                selectedColor="rgba(255, 255, 255, 0.10)"
                borderRadius="4px"
              >
                Students
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>

          <LoggedOutBurgerMenu
            burgerButtonProps={{
              height: [20, 20, 60],
              width: [25, 25, 60],
              display: ["flex", "flex", "none"],
              ml: 20
            }}
          />
        </Group>
      </Group>
    </PanelBoxV2>
  );
}
